import { supabase } from '@/integrations/supabase/client';

export interface AgendaOSItem {
  duracao_padrao_minutos: number | null;
  quantidade: number;
  servico_nome: string | null;
}

export interface AgendaOSPrestador {
  id: string;
  nome: string;
}

export interface AgendaOSCliente {
  id: string;
  nome: string;
}

export interface AgendaOS {
  id: string;
  numero_os: string;
  data_agendada: string | null;
  duracao_estimada_minutos: number | null;
  prestador_id: string | null;
  cliente_id: string | null;
  status: string;
  organization_id: string;
  prestador: AgendaOSPrestador | null;
  cliente: AgendaOSCliente | null;
  itens: AgendaOSItem[];
}

type RawPrestador = { id: string; profile: { nome: string } | null } | null;
type RawCliente = { id: string; nome: string | null } | null;
type RawItem = {
  quantidade: number;
  servico_nome: string | null;
  servico: { duracao_padrao_minutos: number | null } | null;
};

const AGENDA_OS_SELECT = `
  id,
  numero_os,
  data_agendada,
  duracao_estimada_minutos,
  prestador_id,
  cliente_id,
  status,
  organization_id,
  prestador:prestadores_servico(id, profile:profiles(nome)),
  cliente:clientes_potenciais(id, nome),
  itens:ordem_servico_itens(quantidade, servico_nome, servico:servicos(duracao_padrao_minutos))
`;

function flattenPrestador(raw: RawPrestador): AgendaOSPrestador | null {
  if (!raw) return null;
  return {
    id: raw.id,
    nome: raw.profile?.nome ?? '',
  };
}

function flattenCliente(raw: RawCliente): AgendaOSCliente | null {
  if (!raw) return null;
  return { id: raw.id, nome: raw.nome ?? '' };
}

function flattenItens(raw: RawItem[] | null): AgendaOSItem[] {
  if (!raw) return [];
  return raw.map((r) => ({
    duracao_padrao_minutos: r.servico?.duracao_padrao_minutos ?? null,
    quantidade: r.quantidade,
    servico_nome: r.servico_nome ?? null,
  }));
}

export async function listOSByDateRange(params: {
  orgId: string;
  start: Date;
  end: Date;
}): Promise<AgendaOS[]> {
  const { data, error } = await supabase
    .from('ordens_servico')
    .select(AGENDA_OS_SELECT)
    .eq('organization_id', params.orgId)
    .gte('data_agendada', params.start.toISOString())
    .lte('data_agendada', params.end.toISOString())
    .order('data_agendada', { ascending: true });
  if (error) throw error;
  if (!data) return [];
  return data.map((row) => {
    const raw = row as unknown as {
      id: string;
      numero_os: string;
      data_agendada: string | null;
      duracao_estimada_minutos: number | null;
      prestador_id: string | null;
      cliente_id: string | null;
      status: string;
      organization_id: string;
      prestador: RawPrestador;
      cliente: RawCliente;
      itens: RawItem[] | null;
    };
    return {
      id: raw.id,
      numero_os: raw.numero_os,
      data_agendada: raw.data_agendada,
      duracao_estimada_minutos: raw.duracao_estimada_minutos,
      prestador_id: raw.prestador_id,
      cliente_id: raw.cliente_id,
      status: raw.status,
      organization_id: raw.organization_id,
      prestador: flattenPrestador(raw.prestador),
      cliente: flattenCliente(raw.cliente),
      itens: flattenItens(raw.itens),
    };
  });
}

